import { css } from "@emotion/react";
import styled from "@emotion/styled";
import { GrTypography, palette } from "@jnpmedi/grebob";
import { StyledSignIndicator } from "@newComponents/common/Common.styled";
import { defaultMarkerColor, getColorBySignerIndex } from "@newComponents/common/Viewer/types";
import { getSignatureImageBase64 } from "@newComponents/common/Viewer/Viewer.function";
import { MvnMessage } from "@newLibs/utils";
import { baseHeight } from "@assets/theme/baseSize/baseHeight";
import { baseWidth } from "@assets/theme/baseSize/baseWidth";
import { EConsentMarkerProps } from "./EConsentMarker";
import { COMMON_LANG } from "@maven-msg/lang/common";
import { DOCS_DOCUMENT_LANG } from "@maven-msg/lang/docs";
import { DocumentSignerSignature } from "@maven-rest/docs/DocumentSign.dto";

export function SignMarker(props: EConsentMarkerProps) {
  const signature = props.signatures?.find((e) => e.attrUuid === props.marker.attrUuid);

  return (
    <StyledSign
      {...props}
      isSigned={!!signature}
      onClick={props.onClick}
      signature={signature}
      data-label={MvnMessage(DOCS_DOCUMENT_LANG.DOCUMENT_SIGN_SIGNATURE_ANIMATION_DESC)}
    >
      {props.isModeSigning &&
        (!!signature ? (
          <SignatureResult width={signature?.width} height={signature?.height} url={getSignatureImageBase64(signature)} />
        ) : (
          <GrTypography type="Content.Body04" color={defaultMarkerColor.sign.text}>
            {MvnMessage(COMMON_LANG.SIGN_ALTER)}
          </GrTypography>
        ))}

      {!props.isModeSigning && props.children}
    </StyledSign>
  );
}

export const StyledSign = styled.div<EConsentMarkerProps & { isSigned: boolean; signature?: DocumentSignerSignature }>`
  user-select: none;
  box-sizing: border-box;
  position: absolute;
  display: ${({ isHidden }) => (isHidden ? "none" : "flex")};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  transform: translateY(-50%);
  border-radius: 2px;
  width: ${baseWidth.appMarkerSignatureWidth} !important;
  height: ${baseHeight.appMarkerSignatureHeight} !important;
  outline: none;
  justify-content: center;
  align-items: center;
  font-size: 12px;
  font-weight: 500;
  line-height: 100%;
  white-space: nowrap;
  text-indent: 0;
  cursor: pointer;

  border: ${({ isModeSigning, isSigned, marker }) =>
    isModeSigning || isSigned
      ? `2px solid ${palette[defaultMarkerColor.sign.border]}`
      : `2px solid ${palette[getColorBySignerIndex(marker.signerIndex).border]}`};

  background-color: ${({ isSigned, isModeSigning, marker }) =>
    isSigned ? "transparent" : isModeSigning ? defaultMarkerColor.sign.background : getColorBySignerIndex(marker.signerIndex).background};

  &:hover {
    background-color: ${({ isModeSigning, isSigned, marker }) =>
      isModeSigning || isSigned ? defaultMarkerColor.sign.backgroundHover : getColorBySignerIndex(marker.signerIndex).backgroundHover};

    & > span {
      opacity: 1;
    }
  }

  ${({ isModeSigning, isSigned }) =>
    isModeSigning &&
    css`
      &::after {
        display: ${isSigned ? "none" : "block"};
        ${StyledSignIndicator}
      }
    `}
`;

export const SignatureResult = styled.div<{ url: string; width?: number; height?: number }>`
  ${({ width, height }) => ({
    width,
    height,
  })}
  background-image: url(${({ url }) => url});
  background-size: cover;
  box-sizing: content-box;
  position: relative;
`;
